'use client';

import { c as _c } from "react/compiler-runtime";
import { useActiveWallet, useAuth, useWallets } from '@morfi/backend-sdk-v2/client';
import { useTranslations } from 'next-intl';
import { Fragment } from 'react';
import { UserAvatar } from '@/components/images/user-avatar';
import { UserIdentity } from '@/components/profile/user-identity';
import { WalletStreakChip } from '@/components/streaks/wallet-streak-chip';
import { DropdownMenuItem, DropdownMenuSeparator } from '@/components/ui/app-dropdown-menu';
import { Skeleton } from '@/components/ui/app-skeleton';
import { Icon } from '@/components/ui/icon';
import { ArrowRight01Icon, Logout01Icon, Wallet01Icon } from '@/components/ui/icon/glyphs';
import { PulseDot } from '@/components/ui/pulse-dot';
import { UnavailableWalletBadge } from '@/components/ui/unavailable-wallet-badge';
import { isMorfiMarketsEmail, OPERATOR_DASHBOARD_URL } from '@/config/operator-dashboard';
import { useUserPortfolioSummary, useUserWalletPortfolioSummary } from '@/hooks/positions/user-portfolio/selectors';
import { useNotifyWalletSwitched } from '@/hooks/wallet/use-notify-wallet-switched';
import { Link } from '@/i18n/navigation';
import { useFormattedCurrency } from '@/lib/currency';
import { cn } from '@/lib/ui/utils';
import { useMultiWalletFeature } from '@/providers/multi-wallet-feature-provider';
import { useLayoutStore } from '@/stores/layout';
import type { ConnectedProviderInfo, Wallet as WalletType } from '@/types/auth';
import { DemoModeToggle } from './demo-mode-toggle';
const EXTERNAL_LINK_PROPS = {
  target: '_blank',
  rel: 'noopener noreferrer'
} as const;
export interface WalletSectionContentProps {
  /** Called when the menu should close (after clicking an action / link). */
  onClose?: () => void;
}

// ──────────────────────────────────────────────────────────────────────────
// StatsGrid — three-column Total | Holdings | Balance summary
// ──────────────────────────────────────────────────────────────────────────

interface StatsGridProps {
  userId: string;
  wallets: readonly string[];
}
function StatsGrid(t0) {
  const $ = _c(40);
  const {
    userId,
    wallets
  } = t0;
  const t = useTranslations("header.wallet");
  const {
    formatD6
  } = useFormattedCurrency();
  let t1;
  if ($[0] !== userId || $[1] !== wallets) {
    t1 = {
      userId,
      wallets
    };
    $[0] = userId;
    $[1] = wallets;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const summary = useUserPortfolioSummary(t1);
  const cashD6 = summary.isError ? null : summary.cashD6;
  const holdingsD6 = summary.isError ? null : summary.holdingsD6;
  const totalD6 = summary.isError ? null : summary.totalD6;
  let t2;
  if ($[3] !== t) {
    t2 = t("total");
    $[3] = t;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  let t3;
  if ($[5] !== t2) {
    t3 = <span className="text-caption text-foreground-tertiary">{t2}</span>;
    $[5] = t2;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  let t4;
  if ($[7] !== formatD6 || $[8] !== summary.isPending || $[9] !== totalD6) {
    t4 = summary.isPending ? <Skeleton className="h-4 w-16 self-start" /> : totalD6 === null ? <span data-testid="stats-total" className="text-body font-medium font-mono tabular-nums text-foreground-primary">{formatD6("0")}</span> : <span data-testid="stats-total" className="text-body font-medium font-mono tabular-nums text-foreground-primary">{formatD6(totalD6.toString())}</span>;
    $[7] = formatD6;
    $[8] = summary.isPending;
    $[9] = totalD6;
    $[10] = t4;
  } else {
    t4 = $[10];
  }
  let t5;
  if ($[11] !== t3 || $[12] !== t4) {
    t5 = <div className="flex min-w-0 flex-col items-start gap-1 text-left">{t3}{t4}</div>;
    $[11] = t3;
    $[12] = t4;
    $[13] = t5;
  } else {
    t5 = $[13];
  }
  let t6;
  if ($[14] !== t) {
    t6 = t("holdings");
    $[14] = t;
    $[15] = t6;
  } else {
    t6 = $[15];
  }
  let t7;
  if ($[16] !== t6) {
    t7 = <span className="text-caption text-foreground-tertiary">{t6}</span>;
    $[16] = t6;
    $[17] = t7;
  } else {
    t7 = $[17];
  }
  let t8;
  if ($[18] !== formatD6 || $[19] !== holdingsD6 || $[20] !== summary.isPending) {
    t8 = summary.isPending ? <Skeleton className="mx-auto h-4 w-16" /> : holdingsD6 === null ? <span data-testid="stats-holdings" className="text-body font-medium font-mono tabular-nums text-foreground-primary">{formatD6("0")}</span> : <span data-testid="stats-holdings" className="text-body font-medium font-mono tabular-nums text-foreground-primary">{formatD6(holdingsD6.toString())}</span>;
    $[18] = formatD6;
    $[19] = holdingsD6;
    $[20] = summary.isPending;
    $[21] = t8;
  } else {
    t8 = $[21];
  }
  let t9;
  if ($[22] !== t7 || $[23] !== t8) {
    t9 = <div className="flex min-w-0 flex-col items-center gap-1 text-center">{t7}{t8}</div>;
    $[22] = t7;
    $[23] = t8;
    $[24] = t9;
  } else {
    t9 = $[24];
  }
  let t10;
  if ($[25] !== t) {
    t10 = t("cash");
    $[25] = t;
    $[26] = t10;
  } else {
    t10 = $[26];
  }
  let t11;
  if ($[27] !== t10) {
    t11 = <span className="text-caption text-foreground-tertiary">{t10}</span>;
    $[27] = t10;
    $[28] = t11;
  } else {
    t11 = $[28];
  }
  let t12;
  if ($[29] !== cashD6 || $[30] !== formatD6 || $[31] !== summary.isPending) {
    t12 = summary.isPending ? <Skeleton className="h-4 w-16 self-end" /> : cashD6 === null ? <span data-testid="stats-balance" className="text-body font-medium font-mono tabular-nums text-foreground-primary">{formatD6("0")}</span> : <span data-testid="stats-balance" className="text-body font-medium font-mono tabular-nums text-foreground-primary">{formatD6(cashD6.toString())}</span>;
    $[29] = cashD6;
    $[30] = formatD6;
    $[31] = summary.isPending;
    $[32] = t12;
  } else {
    t12 = $[32];
  }
  let t13;
  if ($[33] !== t11 || $[34] !== t12) {
    t13 = <div className="flex min-w-0 flex-col items-end gap-1 text-right">{t11}{t12}</div>;
    $[33] = t11;
    $[34] = t12;
    $[35] = t13;
  } else {
    t13 = $[35];
  }
  let t14;
  if ($[36] !== t13 || $[37] !== t5 || $[38] !== t9) {
    t14 = <div className="grid grid-cols-3 gap-3" data-testid="stats-grid">{t5}{t9}{t13}</div>;
    $[36] = t13;
    $[37] = t5;
    $[38] = t9;
    $[39] = t14;
  } else {
    t14 = $[39];
  }
  return t14;
}

// ──────────────────────────────────────────────────────────────────────────
// HeaderRow — avatar + UserIdentity + animated chevron-right
// ──────────────────────────────────────────────────────────────────────────

interface HeaderRowProps {
  user: {
    id: string;
    displayName: string | null;
    handle: string | null;
    avatarUrl: string | null;
    avatarNsfw?: boolean | null;
    avatarPreferences?: {
      rasterise: boolean;
    } | null;
    connectedProviders: ReadonlyArray<ConnectedProviderInfo>;
  };
  onClose?: (() => void) | undefined;
  className?: string;
  walletPubkeys?: readonly string[];
}
function HeaderRow(t0) {
  const $ = _c(41);
  const {
    user,
    onClose,
    className,
    walletPubkeys
  } = t0;
  const t = useTranslations("header.wallet");
  const t1 = user.id ? `/portfolio/${user.id}` : "/portfolio";
  let t2;
  if ($[0] !== t) {
    t2 = t("view_profile");
    $[0] = t;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  let t3;
  if ($[2] !== className) {
    t3 = cn("group flex w-full min-w-0 items-center gap-3 rounded-overlay-item p-2 outline-none transition-colors hover:bg-background-tertiary focus-visible:bg-background-tertiary", className);
    $[2] = className;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  const t4 = user.avatarNsfw ?? false;
  const t5 = user.avatarPreferences?.rasterise ?? false;
  let t6;
  if ($[4] !== t4 || $[5] !== t5 || $[6] !== user.avatarUrl || $[7] !== user.displayName || $[8] !== user.id) {
    t6 = <UserAvatar size="md" userId={user.id} displayName={user.displayName} avatarUrl={user.avatarUrl} nsfw={t4} rasterise={t5} className="shrink-0" />;
    $[4] = t4;
    $[5] = t5;
    $[6] = user.avatarUrl;
    $[7] = user.displayName;
    $[8] = user.id;
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  const t7 = user.avatarNsfw ?? null;
  const t8 = user.avatarPreferences ?? null;
  let t9;
  if ($[10] !== t7 || $[11] !== t8 || $[12] !== user.avatarUrl || $[13] !== user.connectedProviders || $[14] !== user.displayName || $[15] !== user.handle || $[16] !== user.id) {
    t9 = {
      userId: user.id,
      displayName: user.displayName,
      handle: user.handle,
      avatarUrl: user.avatarUrl,
      avatarNsfw: t7,
      avatarPreferences: t8,
      connectedProviders: user.connectedProviders
    };
    $[10] = t7;
    $[11] = t8;
    $[12] = user.avatarUrl;
    $[13] = user.connectedProviders;
    $[14] = user.displayName;
    $[15] = user.handle;
    $[16] = user.id;
    $[17] = t9;
  } else {
    t9 = $[17];
  }
  let t10;
  if ($[18] !== t) {
    t10 = t("view_profile");
    $[18] = t;
    $[19] = t10;
  } else {
    t10 = $[19];
  }
  let t11;
  if ($[20] !== t10 || $[21] !== t9) {
    t11 = <div className="min-w-0 flex-1"><UserIdentity user={t9} fallback={t10} size="sm" variant="stack" /></div>;
    $[20] = t10;
    $[21] = t9;
    $[22] = t11;
  } else {
    t11 = $[22];
  }
  let t12;
  if ($[23] !== user.id || $[24] !== walletPubkeys) {
    t12 = user.id ? <WalletStreakChip userId={user.id} walletPubkeys={walletPubkeys ?? []} /> : null;
    $[23] = user.id;
    $[24] = walletPubkeys;
    $[25] = t12;
  } else {
    t12 = $[25];
  }
  let t13;
  if ($[26] !== t) {
    t13 = t("profile_chevron_aria_label");
    $[26] = t;
    $[27] = t13;
  } else {
    t13 = $[27];
  }
  let t14;
  if ($[28] !== t13) {
    t14 = <span className="flex w-0 items-center justify-end overflow-hidden opacity-0 transition-all duration-fast ease-position group-hover:w-6 group-hover:opacity-100 group-focus-visible:w-6 group-focus-visible:opacity-100 motion-reduce:w-6 motion-reduce:opacity-100 motion-reduce:transition-none [@media(pointer:coarse)]:w-6 [@media(pointer:coarse)]:opacity-100"><Icon icon={ArrowRight01Icon} aria-label={t13} className="pointer-events-none size-4 shrink-0 text-foreground-tertiary" /></span>;
    $[28] = t13;
    $[29] = t14;
  } else {
    t14 = $[29];
  }
  let t15;
  if ($[30] !== t12 || $[31] !== t14) {
    t15 = <div className="flex shrink-0 items-center">{t12}{t14}</div>;
    $[30] = t12;
    $[31] = t14;
    $[32] = t15;
  } else {
    t15 = $[32];
  }
  let t16;
  if ($[33] !== onClose || $[34] !== t1 || $[35] !== t11 || $[36] !== t15 || $[37] !== t2 || $[38] !== t3 || $[39] !== t6) {
    t16 = <Link href={t1} onClick={onClose} data-testid="header-row-link" aria-label={t2} className={t3}>{t6}{t11}{t15}</Link>;
    $[33] = onClose;
    $[34] = t1;
    $[35] = t11;
    $[36] = t15;
    $[37] = t2;
    $[38] = t3;
    $[39] = t6;
    $[40] = t16;
  } else {
    t16 = $[40];
  }
  return t16;
}

// ──────────────────────────────────────────────────────────────────────────
// WalletRow — per-wallet card body (used by both variants)
// ──────────────────────────────────────────────────────────────────────────

interface WalletRowContentProps {
  wallet: WalletType;
  isActive: boolean;
  userId: string;
}
function WalletRowBody(t0) {
  const $ = _c(51);
  const {
    wallet,
    isActive,
    userId
  } = t0;
  const t = useTranslations("header.wallet");
  const {
    formatD6
  } = useFormattedCurrency();
  let t1;
  if ($[0] !== userId || $[1] !== wallet.address) {
    t1 = {
      userId,
      wallet: wallet.address
    };
    $[0] = userId;
    $[1] = wallet.address;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const summary = useUserWalletPortfolioSummary(t1);
  const unavailable = summary.isPending || summary.isError;
  const cashD6 = unavailable ? null : summary.cashD6;
  const holdingsD6 = unavailable ? null : summary.holdingsD6;
  const totalD6 = unavailable ? null : summary.totalD6;
  const t2 = isActive ? "text-foreground-primary" : "text-foreground-tertiary";
  let t3;
  if ($[3] !== t2) {
    t3 = cn("flex w-full flex-col gap-1 leading-tight", t2);
    $[3] = t2;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  let t4;
  if ($[5] !== isActive || $[6] !== wallet.label) {
    t4 = isActive ? <div className="flex min-w-0 flex-1 items-center gap-2 pr-2"><PulseDot size="md" color="success" /><span className="truncate text-body font-medium">{wallet.label}</span></div> : <span className="min-w-0 flex-1 truncate text-body font-medium">{wallet.label}</span>;
    $[5] = isActive;
    $[6] = wallet.label;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  let t5;
  if ($[8] !== formatD6 || $[9] !== totalD6) {
    t5 = formatD6((totalD6 ?? BigInt(0)).toString());
    $[8] = formatD6;
    $[9] = totalD6;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  let t6;
  if ($[11] !== t5) {
    t6 = <span className="shrink-0 text-body font-medium font-mono tabular-nums">{t5}</span>;
    $[11] = t5;
    $[12] = t6;
  } else {
    t6 = $[12];
  }
  let t7;
  if ($[13] !== t4 || $[14] !== t6) {
    t7 = <div className="flex items-center justify-between gap-3">{t4}{t6}</div>;
    $[13] = t4;
    $[14] = t6;
    $[15] = t7;
  } else {
    t7 = $[15];
  }
  let t8;
  if ($[16] !== t || $[17] !== wallet.isAvailable) {
    t8 = wallet.isAvailable === false ? <UnavailableWalletBadge label={t("unavailable_on_device")} className="self-start" /> : null;
    $[16] = t;
    $[17] = wallet.isAvailable;
    $[18] = t8;
  } else {
    t8 = $[18];
  }
  let t9;
  if ($[19] !== t) {
    t9 = t("holdings_label");
    $[19] = t;
    $[20] = t9;
  } else {
    t9 = $[20];
  }
  let t10;
  if ($[21] !== t9) {
    t10 = <span>{t9}</span>;
    $[21] = t9;
    $[22] = t10;
  } else {
    t10 = $[22];
  }
  let t11;
  if ($[23] !== formatD6 || $[24] !== holdingsD6) {
    t11 = formatD6((holdingsD6 ?? BigInt(0)).toString());
    $[23] = formatD6;
    $[24] = holdingsD6;
    $[25] = t11;
  } else {
    t11 = $[25];
  }
  let t12;
  if ($[26] !== t11) {
    t12 = <span className="font-mono">{t11}</span>;
    $[26] = t11;
    $[27] = t12;
  } else {
    t12 = $[27];
  }
  let t13;
  if ($[28] !== t10 || $[29] !== t12) {
    t13 = <span className="flex items-baseline gap-1">{t10}{t12}</span>;
    $[28] = t10;
    $[29] = t12;
    $[30] = t13;
  } else {
    t13 = $[30];
  }
  let t14;
  if ($[31] !== t) {
    t14 = t("cash_label");
    $[31] = t;
    $[32] = t14;
  } else {
    t14 = $[32];
  }
  let t15;
  if ($[33] !== t14) {
    t15 = <span>{t14}</span>;
    $[33] = t14;
    $[34] = t15;
  } else {
    t15 = $[34];
  }
  let t16;
  if ($[35] !== cashD6 || $[36] !== formatD6) {
    t16 = formatD6((cashD6 ?? BigInt(0)).toString());
    $[35] = cashD6;
    $[36] = formatD6;
    $[37] = t16;
  } else {
    t16 = $[37];
  }
  let t17;
  if ($[38] !== t16) {
    t17 = <span className="font-mono">{t16}</span>;
    $[38] = t16;
    $[39] = t17;
  } else {
    t17 = $[39];
  }
  let t18;
  if ($[40] !== t15 || $[41] !== t17) {
    t18 = <span className="flex items-baseline gap-1">{t15}{t17}</span>;
    $[40] = t15;
    $[41] = t17;
    $[42] = t18;
  } else {
    t18 = $[42];
  }
  let t19;
  if ($[43] !== t13 || $[44] !== t18) {
    t19 = <div className="flex items-baseline justify-between gap-3 text-caption tabular-nums">{t13}{t18}</div>;
    $[43] = t13;
    $[44] = t18;
    $[45] = t19;
  } else {
    t19 = $[45];
  }
  let t20;
  if ($[46] !== t19 || $[47] !== t3 || $[48] !== t7 || $[49] !== t8) {
    t20 = <div className={t3}>{t7}{t8}{t19}</div>;
    $[46] = t19;
    $[47] = t3;
    $[48] = t7;
    $[49] = t8;
    $[50] = t20;
  } else {
    t20 = $[50];
  }
  return t20;
}

// ──────────────────────────────────────────────────────────────────────────
// UserSummaryRow — exported helper used by settings/account client
// (unchanged from prior behavior; kept for backwards compatibility)
// ──────────────────────────────────────────────────────────────────────────

export function UserSummaryRow(t0) {
  const $ = _c(49);
  const {
    onNavigate
  } = t0;
  const t = useTranslations("header.wallet");
  const tStats = useTranslations("profile.stats");
  const {
    formatD6
  } = useFormattedCurrency();
  const {
    user
  } = useAuth();
  const {
    wallets
  } = useWallets();
  const {
    enabled: multiWalletsEnabled,
    primaryWallet
  } = useMultiWalletFeature();
  let t1;
  if ($[0] !== multiWalletsEnabled || $[1] !== primaryWallet || $[2] !== wallets) {
    const visibleWallets = multiWalletsEnabled ? wallets : primaryWallet === null ? [] : [primaryWallet];
    t1 = visibleWallets.filter(_temp).map(_temp2);
    $[0] = multiWalletsEnabled;
    $[1] = primaryWallet;
    $[2] = wallets;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const walletAddresses = t1;
  const t2 = user?.id ?? "";
  let t3;
  if ($[4] !== t2 || $[5] !== walletAddresses) {
    t3 = {
      userId: t2,
      wallets: walletAddresses
    };
    $[4] = t2;
    $[5] = walletAddresses;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  const summary = useUserPortfolioSummary(t3);
  const cashD6 = summary.isError ? null : summary.cashD6;
  const holdingsD6 = summary.isError ? null : summary.holdingsD6;
  const totalD6 = summary.isError ? null : summary.totalD6;
  let t4;
  if ($[7] !== formatD6 || $[8] !== summary.isPending || $[9] !== totalD6) {
    t4 = summary.isPending || totalD6 === null ? formatD6("0") : formatD6(totalD6.toString());
    $[7] = formatD6;
    $[8] = summary.isPending;
    $[9] = totalD6;
    $[10] = t4;
  } else {
    t4 = $[10];
  }
  const portfolioBalanceText = t4;
  const showBreakdown = !summary.isPending;
  if (!user) {
    return null;
  }
  const t5 = user.id ? `/portfolio/${user.id}` : "/portfolio";
  let t6;
  if ($[11] !== t) {
    t6 = t("view_profile");
    $[11] = t;
    $[12] = t6;
  } else {
    t6 = $[12];
  }
  const t7 = user.avatarNsfw ?? false;
  const t8 = user.avatarPreferences?.rasterise ?? false;
  let t9;
  if ($[13] !== t7 || $[14] !== t8 || $[15] !== user.avatarUrl || $[16] !== user.displayName || $[17] !== user.id) {
    t9 = <UserAvatar size="md" userId={user.id} displayName={user.displayName} avatarUrl={user.avatarUrl} nsfw={t7} rasterise={t8} className="shrink-0" />;
    $[13] = t7;
    $[14] = t8;
    $[15] = user.avatarUrl;
    $[16] = user.displayName;
    $[17] = user.id;
    $[18] = t9;
  } else {
    t9 = $[18];
  }
  const t10 = user.avatarNsfw === true;
  let t11;
  if ($[19] !== t10 || $[20] !== user.avatarUrl || $[21] !== user.connectedProviders || $[22] !== user.displayName || $[23] !== user.handle || $[24] !== user.id) {
    t11 = {
      userId: user.id,
      displayName: user.displayName,
      handle: user.handle,
      avatarUrl: user.avatarUrl,
      avatarNsfw: t10,
      connectedProviders: user.connectedProviders
    };
    $[19] = t10;
    $[20] = user.avatarUrl;
    $[21] = user.connectedProviders;
    $[22] = user.displayName;
    $[23] = user.handle;
    $[24] = user.id;
    $[25] = t11;
  } else {
    t11 = $[25];
  }
  let t12;
  if ($[26] !== t) {
    t12 = t("view_profile");
    $[26] = t;
    $[27] = t12;
  } else {
    t12 = $[27];
  }
  let t13;
  if ($[28] !== t11 || $[29] !== t12) {
    t13 = <UserIdentity user={t11} fallback={t12} size="sm" variant="stack" />;
    $[28] = t11;
    $[29] = t12;
    $[30] = t13;
  } else {
    t13 = $[30];
  }
  let t14;
  if ($[31] !== portfolioBalanceText) {
    t14 = <div className="text-caption text-foreground-secondary">{portfolioBalanceText}</div>;
    $[31] = portfolioBalanceText;
    $[32] = t14;
  } else {
    t14 = $[32];
  }
  let t15;
  if ($[33] !== cashD6 || $[34] !== formatD6 || $[35] !== holdingsD6 || $[36] !== showBreakdown || $[37] !== tStats) {
    t15 = showBreakdown ? <div className="flex items-baseline gap-1 text-caption text-foreground-secondary tabular-nums"><span>{tStats("cash")} {formatD6((cashD6 ?? BigInt(0)).toString())}</span><span aria-hidden="true">·</span><span>{tStats("holdings")} {formatD6((holdingsD6 ?? BigInt(0)).toString())}</span></div> : null;
    $[33] = cashD6;
    $[34] = formatD6;
    $[35] = holdingsD6;
    $[36] = showBreakdown;
    $[37] = tStats;
    $[38] = t15;
  } else {
    t15 = $[38];
  }
  let t16;
  if ($[39] !== t13 || $[40] !== t14 || $[41] !== t15) {
    t16 = <div className="min-w-0 flex-1">{t13}{t14}{t15}</div>;
    $[39] = t13;
    $[40] = t14;
    $[41] = t15;
    $[42] = t16;
  } else {
    t16 = $[42];
  }
  let t17;
  if ($[43] !== onNavigate || $[44] !== t16 || $[45] !== t5 || $[46] !== t6 || $[47] !== t9) {
    t17 = <Link href={t5} onClick={onNavigate} className="flex w-full min-w-0 cursor-default items-center gap-3 rounded-overlay-item px-2 py-2 outline-none hover:bg-background-tertiary hover:text-foreground-tertiary" aria-label={t6}>{t9}{t16}</Link>;
    $[43] = onNavigate;
    $[44] = t16;
    $[45] = t5;
    $[46] = t6;
    $[47] = t9;
    $[48] = t17;
  } else {
    t17 = $[48];
  }
  return t17;
}

// ──────────────────────────────────────────────────────────────────────────
// WalletSectionContent — main component
// ──────────────────────────────────────────────────────────────────────────
function _temp2(wallet_0) {
  return wallet_0.address;
}
function _temp(wallet) {
  return wallet.archivedAt === null;
}
export function WalletSectionContent(t0) {
  const $ = _c(44);
  const {
    onClose
  } = t0;
  const t = useTranslations("header.wallet");
  const tDashboard = useTranslations("rewards.userMenu");
  const {
    activeWallet,
    setActiveWalletId
  } = useActiveWallet();
  const {
    user,
    logout
  } = useAuth();
  const {
    wallets
  } = useWallets();
  const {
    enabled: multiWalletsEnabled,
    primaryWallet
  } = useMultiWalletFeature();
  const notifyWalletSwitched = useNotifyWalletSwitched();
  const gateVisibleWallets = multiWalletsEnabled ? wallets : primaryWallet === null ? [] : [primaryWallet];
  const ownWallets = gateVisibleWallets.filter(_temp3).sort(_temp4);
  const walletAddresses = ownWallets.map(_temp5);
  const setManageWalletsOpen = useLayoutStore(_temp6);
  const active = activeWallet !== null ? ownWallets.find(w_1 => w_1.id === activeWallet.id) : undefined;
  const others = ownWallets.filter(w_2 => w_2.id !== active?.id).slice(0, active ? 2 : 3);
  const visibleWallets = active !== undefined ? [active, ...others] : others;
  const showWalletList = multiWalletsEnabled && ownWallets.length > 1;
  let t1;
  if ($[0] !== onClose || $[1] !== setManageWalletsOpen) {
    t1 = function handleManageWallets() {
      setManageWalletsOpen(true);
      onClose?.();
    };
    $[0] = onClose;
    $[1] = setManageWalletsOpen;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const handleManageWallets = t1;
  let t2;
  if ($[3] !== logout || $[4] !== onClose) {
    t2 = function handleLogout() {
      onClose?.();
      logout();
    };
    $[3] = logout;
    $[4] = onClose;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  const handleLogout = t2;
  let t3;
  if ($[6] !== activeWallet?.id || $[7] !== notifyWalletSwitched || $[8] !== setActiveWalletId) {
    t3 = function handleSelectWallet(walletId, walletLabel, isActive, isAvailable) {
      if (isActive || !isAvailable) {
        return;
      }
      setActiveWalletId(walletId);
      notifyWalletSwitched({
        fromWalletId: activeWallet?.id ?? null,
        toWalletId: walletId,
        label: walletLabel
      });
    };
    $[6] = activeWallet?.id;
    $[7] = notifyWalletSwitched;
    $[8] = setActiveWalletId;
    $[9] = t3;
  } else {
    t3 = $[9];
  }
  const handleSelectWallet = t3;
  const t4 = user?.id ?? "";
  const t5 = user?.displayName ?? null;
  const t6 = user?.handle ?? null;
  const t7 = user?.avatarUrl ?? null;
  const t8 = user?.avatarNsfw ?? null;
  const t9 = user?.avatarPreferences ?? null;
  let t10;
  if ($[10] !== user?.connectedProviders) {
    t10 = user?.connectedProviders ?? [];
    $[10] = user?.connectedProviders;
    $[11] = t10;
  } else {
    t10 = $[11];
  }
  let t11;
  if ($[12] !== t10 || $[13] !== t4 || $[14] !== t5 || $[15] !== t6 || $[16] !== t7 || $[17] !== t8 || $[18] !== t9) {
    t11 = {
      id: t4,
      displayName: t5,
      handle: t6,
      avatarUrl: t7,
      avatarNsfw: t8,
      avatarPreferences: t9,
      connectedProviders: t10
    };
    $[12] = t10;
    $[13] = t4;
    $[14] = t5;
    $[15] = t6;
    $[16] = t7;
    $[17] = t8;
    $[18] = t9;
    $[19] = t11;
  } else {
    t11 = $[19];
  }
  const headerUser = t11;
  const t12 = user?.id ?? "";
  let t13;
  if ($[20] !== t12 || $[21] !== walletAddresses) {
    t13 = <div className="px-2"><StatsGrid userId={t12} wallets={walletAddresses} /></div>;
    $[20] = t12;
    $[21] = walletAddresses;
    $[22] = t13;
  } else {
    t13 = $[22];
  }
  let t14;
  if ($[23] === Symbol.for("react.memo_cache_sentinel")) {
    t14 = <DemoModeToggle />;
    $[23] = t14;
  } else {
    t14 = $[23];
  }
  let t15;
  if ($[24] !== onClose || $[25] !== tDashboard || $[26] !== user?.email) {
    t15 = isMorfiMarketsEmail(user?.email) ? <DropdownMenuItem asChild={true}><a href={OPERATOR_DASHBOARD_URL} {...EXTERNAL_LINK_PROPS} onClick={onClose}><Icon icon={ArrowRight01Icon} className="size-4" />{tDashboard("dashboard")}</a></DropdownMenuItem> : null;
    $[24] = onClose;
    $[25] = tDashboard;
    $[26] = user?.email;
    $[27] = t15;
  } else {
    t15 = $[27];
  }
  let t16;
  if ($[28] === Symbol.for("react.memo_cache_sentinel")) {
    t16 = <Icon icon={Wallet01Icon} className="size-4" />;
    $[28] = t16;
  } else {
    t16 = $[28];
  }
  let t17;
  if ($[29] !== t) {
    t17 = t("manage_wallets");
    $[29] = t;
    $[30] = t17;
  } else {
    t17 = $[30];
  }
  let t18;
  if ($[31] !== handleManageWallets || $[32] !== t17) {
    t18 = <DropdownMenuItem onSelect={handleManageWallets}>{t16}{t17}</DropdownMenuItem>;
    $[31] = handleManageWallets;
    $[32] = t17;
    $[33] = t18;
  } else {
    t18 = $[33];
  }
  let t19;
  if ($[34] === Symbol.for("react.memo_cache_sentinel")) {
    t19 = <Icon icon={Logout01Icon} className="size-4" />;
    $[34] = t19;
  } else {
    t19 = $[34];
  }
  let t20;
  if ($[35] !== t) {
    t20 = t("logout");
    $[35] = t;
    $[36] = t20;
  } else {
    t20 = $[36];
  }
  let t21;
  if ($[37] !== handleLogout || $[38] !== t20) {
    t21 = <DropdownMenuItem variant="destructive" onSelect={handleLogout}>{t19}{t20}</DropdownMenuItem>;
    $[37] = handleLogout;
    $[38] = t20;
    $[39] = t21;
  } else {
    t21 = $[39];
  }
  let t22;
  if ($[40] !== t15 || $[41] !== t18 || $[42] !== t21) {
    t22 = <div className="flex flex-col">{t14}{t15}{t18}{t21}</div>;
    $[40] = t15;
    $[41] = t18;
    $[42] = t21;
    $[43] = t22;
  } else {
    t22 = $[43];
  }
  return <div className="flex flex-col gap-3 p-1"><div className="flex flex-col gap-1.5"><HeaderRow user={headerUser} onClose={onClose} walletPubkeys={walletAddresses} />{t13}</div>{showWalletList ? <div data-testid="wallet-list" className="flex max-h-[40vh] gap-y-1 flex-col overflow-y-auto scrollbar-none"><DropdownMenuSeparator className="h-[0.5px] mx-2 bg-border-primary" />{visibleWallets.map(w_3 => {
        const isActive_0 = activeWallet !== null && w_3.id === activeWallet.id;
        return <Fragment key={w_3.id}><DropdownMenuItem data-testid={`wallet-row-${w_3.id}`} data-active={isActive_0 ? "true" : "false"} disabled={w_3.isAvailable === false} onSelect={() => handleSelectWallet(w_3.id, w_3.label, isActive_0, w_3.isAvailable !== false)}><WalletRowBody wallet={w_3} isActive={isActive_0} userId={user?.id ?? ""} /></DropdownMenuItem><DropdownMenuSeparator className="h-[0.5px] mx-2 bg-border-primary" /></Fragment>;
      })}</div> : null}{t22}</div>;
}
function _temp6(s) {
  return s.setManageWalletsOpen;
}
function _temp5(w_0) {
  return w_0.address;
}
function _temp4(a, b) {
  return (a.createdAt ?? "").localeCompare(b.createdAt ?? "");
}
function _temp3(w) {
  return w.archivedAt === null;
}